import React, { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
    BLOCK_LABELS, documentBlocks, ToolContentDocument, ToolContentKind, ToolDetailBlockView, tryParseDocument,
} from '@easytools/tool-content';
import { diffBlocks, ORIGIN_LABELS, ToolContentVersion, formatWhen } from './contentModel';

const CHANGE_LABELS = { added: 'Aggiunto', removed: 'Rimosso', changed: 'Modificato', moved: 'Spostato' } as const;

/** Conferma della pubblicazione: mostra cosa cambia rispetto alla versione online, blocco per blocco. */
export const PublishDialog: React.FC<{
    show: boolean;
    title: string;
    version: number;
    published: ToolContentDocument | null;
    draft: ToolContentDocument;
    warning?: string;
    busy: boolean;
    onConfirm: () => void;
    onClose: () => void;
}> = ({ show, title, version, published, draft, warning, busy, onConfirm, onClose }) => {
    const changes = useMemo(() => diffBlocks(published ? documentBlocks(published) : [], documentBlocks(draft)), [published, draft]);
    const edited = changes.filter(c => c.type !== 'same');
    const unchanged = changes.length - edited.length;
    // Sezioni e impostazioni non sono blocchi: basta dire che sono cambiate.
    const layoutChanged = published !== null && JSON.stringify(published.sections.map(({ blocks, ...style }) => [style, blocks.length]))
        !== JSON.stringify(draft.sections.map(({ blocks, ...style }) => [style, blocks.length]));
    const pageChanged = published !== null && JSON.stringify(published.page) !== JSON.stringify(draft.page);

    return (
        <Modal show={show} onHide={onClose} size="xl" scrollable>
            <Modal.Header closeButton>
                <Modal.Title as="h2" className="h5">Pubblica {title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {warning && <div className="alert alert-warning small">{warning}</div>}
                {published === null
                    ? <p className="small text-body-secondary">È la prima versione pubblicata: tutti i blocchi sono nuovi.</p>
                    : <p className="small text-body-secondary">
                        {edited.length === 0
                            ? 'Nessuna differenza rispetto alla versione online.'
                            : `${edited.length} ${edited.length === 1 ? 'blocco cambia' : 'blocchi cambiano'} rispetto alla versione online${unchanged ? `, ${unchanged} restano uguali` : ''}.`}
                        {layoutChanged && ' Cambiano anche le sezioni o il loro stile.'}
                        {pageChanged && ' Cambiano le impostazioni della pagina.'}
                    </p>}
                <ol className="tcs-diff">
                    {edited.map((change, i) => (
                        <li key={i} className={`tcs-diff__item tcs-diff__item--${change.type}`}>
                            <div className="tcs-diff__head">
                                <span className={`tcs-chip tcs-chip--${change.type}`}>{CHANGE_LABELS[change.type as keyof typeof CHANGE_LABELS]}</span>
                                <span>{BLOCK_LABELS[(change.type === 'removed' ? change.before : change.after).type]}</span>
                            </div>
                            {change.type === 'changed' ? (
                                <div className="tcs-diff__pair">
                                    <div><p className="tcs-diff__label">Online</p><div className="tcs-diff__render"><ToolDetailBlockView block={change.before} /></div></div>
                                    <div><p className="tcs-diff__label">Dopo la pubblicazione</p><div className="tcs-diff__render"><ToolDetailBlockView block={change.after} /></div></div>
                                </div>
                            ) : (
                                <div className="tcs-diff__render">
                                    <ToolDetailBlockView block={change.type === 'removed' ? change.before : change.after} />
                                </div>
                            )}
                        </li>
                    ))}
                </ol>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={onClose}>Annulla</button>
                <button type="button" className="btn btn-primary" disabled={busy} onClick={onConfirm}>
                    {busy ? <><span className="spinner-border spinner-border-sm me-2" />Pubblico…</> : `Pubblica la versione ${version}`}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

/** JSON del contenuto: per il debug e per incollare un documento prodotto altrove. */
export const JsonDialog: React.FC<{
    show: boolean;
    kind: ToolContentKind;
    doc: ToolContentDocument;
    readOnly: boolean;
    onReplace: (doc: ToolContentDocument) => void;
    onClose: () => void;
}> = ({ show, kind, doc, readOnly, onReplace, onClose }) => {
    const initial = useMemo(() => JSON.stringify(doc, null, 2), [doc]);
    const [text, setText] = useState(initial);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1600);
        } catch {
            setError('Copia non riuscita: seleziona il testo e copialo a mano.');
        }
    };
    const replace = () => {
        const parsed = tryParseDocument(text, kind);
        if (!parsed) {
            setError('Il JSON non è valido o contiene blocchi non ammessi in questo contenuto.');
            return;
        }
        onReplace(parsed);
        onClose();
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" onEnter={() => { setText(initial); setError(null); }}>
            <Modal.Header closeButton>
                <Modal.Title as="h2" className="h5">JSON del contenuto</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="small text-body-secondary">
                    È il documento salvato nel database: impostazioni della pagina e sezioni con i loro blocchi. Serve per
                    controllarlo o per incollarne uno preparato altrove. Sostituirlo modifica solo la bozza: niente va online finché non pubblichi.
                </p>
                <label htmlFor="tcs-json" className="visually-hidden">JSON del documento</label>
                <textarea id="tcs-json" className="form-control tcs-json" rows={18} value={text} readOnly={readOnly}
                    onChange={e => { setText(e.target.value); setError(null); }} />
                {error && <p className="text-danger small mt-2 mb-0" role="alert">{error}</p>}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={copy}>{copied ? 'Copiato' : 'Copia'}</button>
                {!readOnly && <button type="button" className="btn btn-primary" disabled={text === initial} onClick={replace}>Sostituisci il documento</button>}
            </Modal.Footer>
        </Modal>
    );
};

/** Storico delle versioni di un contenuto in una lingua. */
export const HistoryPanel: React.FC<{
    versions: ToolContentVersion[] | null;
    viewing: number | null;
    onView: (version: ToolContentVersion) => void;
    onRestore: (version: ToolContentVersion) => void;
    onClose: () => void;
}> = ({ versions, viewing, onView, onRestore, onClose }) => (
    <aside className="tcs-panel" aria-label="Storico delle versioni">
        <div className="tcs-panel__head">
            <h3 className="tcs-panel__title">Storico</h3>
            <button type="button" className="btn btn-sm btn-light" onClick={onClose} aria-label="Chiudi lo storico"><i className="bi bi-x-lg" /></button>
        </div>
        <div className="tcs-panel__body">
            {versions === null && <p className="small text-body-secondary">Caricamento…</p>}
            {versions?.length === 0 && <p className="small text-body-secondary">Ancora nessuna versione.</p>}
            <ol className="tcs-history">
                {versions?.map(v => (
                    <li key={v.id} className={`tcs-history__item${viewing === v.version ? ' is-viewing' : ''}`}>
                        <div className="tcs-history__top">
                            <strong>Versione {v.version}</strong>
                            <span className={`tcs-chip tcs-chip--${v.state}`}>{v.state === 'published' ? 'Pubblicata' : v.state === 'draft' ? 'Bozza' : 'Archiviata'}</span>
                        </div>
                        <p className="tcs-history__meta">
                            {v.state === 'draft' ? `Salvata ${formatWhen(v.updatedAt)}` : `Pubblicata ${formatWhen(v.publishedAt)}`}
                            {(v.publishedBy || v.createdBy) && ` · ${v.publishedBy || v.createdBy}`}
                            <br />{ORIGIN_LABELS[v.origin]}{v.sourceVersion ? ` dalla versione italiana ${v.sourceVersion}` : ''}
                            {v.notes && <><br />{v.notes}</>}
                        </p>
                        {v.state !== 'draft' && (
                            <div className="tcs-history__actions">
                                <button type="button" className="btn btn-sm btn-light" onClick={() => onView(v)}>Visualizza</button>
                                <button type="button" className="btn btn-sm btn-light" onClick={() => onRestore(v)}>Ripristina in bozza</button>
                            </div>
                        )}
                    </li>
                ))}
            </ol>
        </div>
    </aside>
);
